import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Search, Plus, FileText, ChevronRight, X, Rocket, Award, Briefcase, Download, Share2, CheckCircle2, Mail, Sparkles, Phone, MapPin, Linkedin, Copy, ExternalLink, GraduationCap, Languages, AlertTriangle, UploadCloud, Loader2, ChevronDown, Check, Trash2, Brain, Pencil, Save, ArrowUpDown, Calendar, Info } from 'lucide-react';
import CandidateDetailPanel from './CandidateDetailPanel';
import CandidatePresentation from './CandidatePresentation';
import type { AppView, TalentProfile } from '../../types';
import FileDropZone from '../shared/FileDropZone';
import { useCandidates, type PortfolioScope } from '../../hooks/useCandidates';
import { useOrganization } from '../../hooks/useOrganization';
import { useProcessing } from '../../context/ProcessingContext';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import { useOrgMembers } from '../../hooks/useOrgMembers';
import ScopeToggle from '../shared/ScopeToggle';
import AssignDropdown from '../shared/AssignDropdown';
// import { toast } from 'sonner'; // DEPRECATED useNotifications instead
import AnalysisProgressWidget from '../analysis/AnalysisProgressWidget';
import BatchImportWidget from '../analysis/BatchImportWidget';

import { useShare } from '../../hooks/useShare';
import { supabase } from '../../lib/supabase';
import { useUpload, BATCH_MODE_THRESHOLD } from '../../hooks/useUpload';
import { useGoogleDrive } from '../../hooks/useGoogleDrive';

// Local type extension to handle UI states and calculated score
type DisplayProfile = TalentProfile & {
    status?: 'analyzing' | 'ready';
    calculatedScore?: number;
};

// Helper to calculate average score from skills
const calculateScore = (profile: TalentProfile): number => {
    // If the profile already has a score from the backend, use it
    if (profile.score !== undefined && profile.score !== null) {
        return profile.score;
    }

    const allSkills = [
        ...profile.skills.technical,
        ...profile.skills.methods,
        ...profile.skills.soft
    ];
    if (allSkills.length === 0) return 0;
    const total = allSkills.reduce((acc, skill) => acc + skill.level, 0);
    return Math.round(total / allSkills.length);
};

// Helper to parse experience string to number (e.g., "10 ans" -> 10)
const parseExperienceYears = (expStr: string): number => {
    return parseInt(expStr.replace(/\D/g, '')) || 0;
};

// Helper to compute SHA-256 hash of a file for content-based deduplication
const computeFileHash = async (file: File): Promise<string> => {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

// Factory for a placeholder profile used during the "Add Candidate" analysis simulation
const createPlaceholderProfile = (): TalentProfile => ({
    id: `temp-${Date.now()}`,
    name: 'Analyse en cours...',
    role: 'Extraction des données...',
    experience: '',
    seniority_level: '',
    badges: [],
    summary: '',
    profile_summary: '',
    additional_insight: '',
    role_fit_titles: [],
    top_strengths: '',
    skills: { technical: [], methods: [], soft: [] },
    differentiator: '',
    timeline: [],
    contactInfo: { phone: '', email: '', location_city: '', linkedin_url: '' },
    topAchievements: [],
    languages: [],
    education: [],
    certifications: [],
    alerts: []
});

interface Toast {
    id: string;
    message: string;
    type: 'info' | 'success' | 'error';
}

interface TalentPoolViewProps {
    onChangeView?: (view: AppView) => void;
    onSelectCandidate?: (candidate: any) => void;
    initialCandidateId?: string | null;
}

const TalentPoolView: React.FC<TalentPoolViewProps> = ({ onChangeView, onSelectCandidate, initialCandidateId }) => {
    const { id: urlCandidateId } = useParams<{ id?: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    // URL param takes priority over prop
    const effectiveCandidateId = urlCandidateId ?? initialCandidateId;

    // Scope portfolio
    const { user } = useAuth();
    const { org } = useOrganization();
    const [searchParams, setSearchParams] = useSearchParams();
    const [scope, setScope] = useState<PortfolioScope>((searchParams.get('scope') as PortfolioScope) || 'portfolio');
    const { members: orgMembers } = useOrgMembers();

    const handleScopeChange = (newScope: PortfolioScope) => {
        setScope(newScope);
        setSearchParams(prev => { prev.set('scope', newScope); return prev; }, { replace: true });
    };

    // Use Custom Hook for Data Fetching
    const { candidates: fetchedCandidates, isLoading: isCandidatesLoading, error: candidatesError, refresh: refreshCandidates, updateCandidate, fetchCandidateDetail } = useCandidates(scope, user?.id);
    const { notify } = useNotifications();

    const { generateLink, isLoading: isShareLoading, error: shareError } = useShare();
    const { uploadCandidate, uploadCandidates, batchProgress, isUploading, error } = useUpload();
    const { pick: pickDriveFiles, isPicking: isDrivePicking, progress: driveProgress, configured: isDriveConfigured } = useGoogleDrive();

    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState<'date' | 'name' | 'experience'>('date'); // 'date' | 'name' | 'experience'
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc'); // 'asc' | 'desc'
    const [selectedProfile, setSelectedProfile] = useState<DisplayProfile | null>(null);
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [shareLink, setShareLink] = useState<string | null>(null);

    // Contact Editing State
    const [isEditingContact, setIsEditingContact] = useState(false);
    const [editedContact, setEditedContact] = useState<any>({});
    const [isSavingContact, setIsSavingContact] = useState(false);

    // Presentation modal state
    const [isPresentationOpen, setIsPresentationOpen] = useState(false);
    const [isExportingPresentation, setIsExportingPresentation] = useState(false);
    const [isPresentationAnonymized, setIsPresentationAnonymized] = useState(false);

    // Availability & TJM Editing State
    const [isEditingAvailability, setIsEditingAvailability] = useState(false);
    const [isSavingAvailability, setIsSavingAvailability] = useState(false);
    const [editedAvailability, setEditedAvailability] = useState<{
        availability_status: 'disponible' | 'listening' | 'unavailable' | null;
        availability_type: 'freelance' | 'cdi' | 'both' | null;
        availability_date: string | null;
        tjm_min: number | null;
        tjm_max: number | null;
    }>({ availability_status: null, availability_type: null, availability_date: null, tjm_min: null, tjm_max: null });

    // Initialize edited contact when modal opens or profile changes
    useEffect(() => {
        if (selectedProfile) {
            setEditedContact({
                phone: selectedProfile.contactInfo.phone,
                location_city: selectedProfile.contactInfo.location_city,
                linkedin_url: selectedProfile.contactInfo.linkedin_url,
                email: selectedProfile.contactInfo.email // Read-only mostly
            });
            setEditedAvailability({
                availability_status: selectedProfile.availability_status ?? null,
                availability_type: selectedProfile.availability_type ?? null,
                availability_date: selectedProfile.availability_date ?? null,
                tjm_min: selectedProfile.tjm_min ?? null,
                tjm_max: selectedProfile.tjm_max ?? null,
            });
        }
    }, [selectedProfile]);


    const handleShare = async (isAnonymized: boolean = false) => {
        if (!selectedProfile) return;
        const link = await generateLink(selectedProfile.id);
        if (link) {
            setShareLink(isAnonymized ? `${link}&anon=1` : link);
            setIsShareModalOpen(true);
        } else {
            notify('error', 'Impossible de générer le lien de partage', {
                description: shareError || 'Vérifiez votre connexion ou contactez le support.',
            });
        }
    };

    const handleSaveAvailability = async () => {
        if (!selectedProfile || !updateCandidate) return;
        try {
            setIsSavingAvailability(true);
            await updateCandidate(selectedProfile.id, editedAvailability);
            setIsEditingAvailability(false);
            notify('success', 'Disponibilité et TJM mis à jour !');
        } catch (err) {
            console.error(err);
            notify('error', 'Erreur lors de la sauvegarde.');
        } finally {
            setIsSavingAvailability(false);
        }
    };

    const handleSaveContact = async () => {
        if (!selectedProfile || !updateCandidate) return;

        try {
            setIsSavingContact(true);
            await updateCandidate(selectedProfile.id, {
                contactInfo: {
                    ...selectedProfile.contactInfo,
                    ...editedContact
                }
            });

            // Optimistic update or wait for refresh
            // The hook calls refresh(), so local state should update automatically via useEffect
            setIsEditingContact(false);
            notify('success', "Coordonnées mises à jour !");
        } catch (err) {
            console.error(err);
            notify('error', "Erreur lors de la sauvegarde.");
        } finally {
            setIsSavingContact(false);
        }
    };

    // State for Candidates List (Local state to support optimistic updates and merging with hook data)
    const [candidates, setCandidates] = useState<DisplayProfile[]>([]);

    // Get processing state to hide incomplete uploads and trigger refreshes
    const { activeUploads, dismissUpload } = useProcessing();

    // Effect to sync local state with fetched data AND filter out processing candidates
    useEffect(() => {
        if (fetchedCandidates) {
            const displayCandidates: DisplayProfile[] = fetchedCandidates
                .filter(p => {
                    // Hide any candidate that is currently in the "activeUploads" list (processing widget)
                    if (activeUploads.has(p.id)) return false;

                    // Hide candidates with placeholder names or explicit processing status
                    // We only want to hide "Unknown" or "Analyse en cours..."
                    if (p.name === 'Unknown' || p.name === 'Analyse en cours...') return false;

                    // Also hide if status is explicitly distinct from 'completed' AND 'error' (e.g. 'received', 'analyzing_...')
                    // BUT be careful not to hide legacy data (where status might be null or missing)
                    if (p.status && ['received', 'analyzing_doc', 'analyzing_agents'].includes(p.status)) return false;

                    return true;
                })
                .map(p => ({
                    ...p,
                    status: 'ready',
                    calculatedScore: calculateScore(p)
                }));
            setCandidates(displayCandidates);
        }
    }, [fetchedCandidates, activeUploads]);

    // Handle URL param / initialCandidateId prop for auto-selection (from navigation)
    useEffect(() => {
        // Pas d'id dans l'URL (ex. retour à /talents via la sidebar) → on ferme la fiche détail
        if (!effectiveCandidateId) { setSelectedProfile(null); return; }
        // Search in display candidates first, then fall back to raw fetchedCandidates.
        // The candidate may still be in activeUploads (filtered out of display) when
        // the user clicks "Voir" from a notification right after analysis completes.
        const candidateToSelect =
            candidates.find(c => c.id === effectiveCandidateId) ??
            fetchedCandidates?.find(c => c.id === effectiveCandidateId);
        if (candidateToSelect && (
            selectedProfile?.id !== effectiveCandidateId ||
            candidateToSelect.updatedAt !== selectedProfile?.updatedAt
        )) {
            setSelectedProfile(candidateToSelect as DisplayProfile);
            fetchCandidateDetail(candidateToSelect.id).then(full => { if (full) setSelectedProfile(full as DisplayProfile); });
        }
    }, [effectiveCandidateId, candidates, fetchedCandidates]);

    // Auto-refresh when an active upload completes, then immediately remove it from
    // activeUploads so the candidate appears in the list without waiting for the 5s auto-dismiss.
    useEffect(() => {
        const completedIds: string[] = [];
        activeUploads.forEach(u => {
            if (u.status === 'completed') completedIds.push(u.id);
            if (u.status === 'duplicate' && u.error) completedIds.push(u.id); // Push the active upload ID to dismiss it properly!
        });

        if (completedIds.length > 0) {
            (async () => {
                await refreshCandidates();
                completedIds.forEach(id => dismissUpload(id)); // Now dismisses the correct temporary ID

                // Find if there is a duplicate redirect target
                let redirectId: string | null = null;
                activeUploads.forEach(u => {
                    if (u.status === 'duplicate' && u.error) redirectId = u.error;
                });

                if (completedIds.length === 1 && redirectId) {
                    await new Promise(r => setTimeout(r, 6000));
                    await refreshCandidates();
                    const full = await fetchCandidateDetail(redirectId);
                    if (full) setSelectedProfile(full as DisplayProfile);
                    navigate(`/talents/${redirectId}`);
                } else if (completedIds.length === 1) {
                    await new Promise(r => setTimeout(r, 6000));
                    await refreshCandidates();
                    const full = await fetchCandidateDetail(completedIds[0]);
                    if (full) setSelectedProfile(full as DisplayProfile);
                    navigate(`/talents/${completedIds[0]}`);
                }
            })();
        }
    }, [activeUploads, refreshCandidates, dismissUpload, navigate]);

    // --- RECRUITER-CENTRIC FILTER STATES ---
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

    // 1. Seniority (Dynamic List)
    const [selectedSeniority, setSelectedSeniority] = useState<string | null>(null);

    // 2. Experience (Ranges)
    const [selectedExperience, setSelectedExperience] = useState<'junior' | 'confirmed' | 'expert' | null>(null);

    // Add Candidate Modal State
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    // Auto-open add modal when navigated from another page with openAdd flag
    useEffect(() => {
        if ((location.state as { openAdd?: boolean } | null)?.openAdd) {
            setIsAddModalOpen(true);
            window.history.replaceState({}, '');
        }
    }, [location.state]);
    const [newCandidateFiles, setNewCandidateFiles] = useState<File[]>([]);
    // Bump à chaque lot envoyé : réveille BatchImportWidget tout de suite au lieu
    // de lui laisser attendre son prochain tour de sondage (15 s).
    const [batchTick, setBatchTick] = useState(0);

    // const [isUploading, setIsUploading] = useState(false); // Removed local state in favor of hook

    // Navigation Handler
    const handleGoToMatching = () => {
        if (selectedProfile) {
            if (onSelectCandidate) onSelectCandidate(selectedProfile);
            navigate('/matching');
        }
    };

    // ... (rest of existing code until button)

    {/* Group C: Matching (Primary) */ }
    <button
        onClick={handleGoToMatching}
        className="flex items-center gap-2 px-5 py-2 bg-amber-600 text-white rounded-lg text-sm font-bold hover:bg-amber-700 shadow-md shadow-amber-900/20 transition-all hover:-translate-y-0.5"
    >
        <Sparkles size={16} />
        Matching
    </button>

    // Toast Notifications State
    const [toasts, setToasts] = useState<Toast[]>([]);

    // --- EXTRACT FILTER OPTIONS (DYNAMIC) ---
    const uniqueSeniority = useMemo(() => {
        const levels = new Set<string>();
        candidates.forEach(c => {
            if (c.seniority_level) levels.add(c.seniority_level);
        });
        return Array.from(levels).sort();
    }, [candidates]);

    // --- SIMPLIFIED FILTER LOGIC & SORTING ---
    const filteredCandidates = useMemo(() => {
        let result = candidates.filter(p => {
            const term = searchTerm.toLowerCase();

            // 1. Search (Name, Role, Badges, Skills)
            const matchesSearch =
                p.name.toLowerCase().includes(term) ||
                p.role.toLowerCase().includes(term) ||
                p.badges.some(b => b.toLowerCase().includes(term)) || // Check Badges
                p.skills.technical.some(skill => skill.name.toLowerCase().includes(term)) ||
                p.skills.methods.some(skill => skill.name.toLowerCase().includes(term)) ||
                p.skills.soft.some(skill => skill.name.toLowerCase().includes(term));

            // 2. Seniority Filter (Exact Match)
            const matchesSeniority = !selectedSeniority || p.seniority_level === selectedSeniority;

            // 3. Experience Filter (Range Logic)
            let matchesExperience = true;
            if (selectedExperience) {
                const years = parseExperienceYears(p.experience);
                if (selectedExperience === 'junior') matchesExperience = years < 3;
                else if (selectedExperience === 'confirmed') matchesExperience = years >= 3 && years <= 7;
                else if (selectedExperience === 'expert') matchesExperience = years > 7;
            }

            // Always show rows that are currently analyzing
            if (p.status === 'analyzing') return true;

            return matchesSearch && matchesSeniority && matchesExperience;
        });

        // --- SORTING ---
        return result.sort((a, b) => {
            let comparison = 0;
            switch (sortBy) {
                case 'name':
                    comparison = a.name.localeCompare(b.name);
                    break;
                case 'experience':
                    comparison = parseExperienceYears(a.experience || '') - parseExperienceYears(b.experience || '');
                    break;
                case 'date':
                default: {
                    // Fallback to date (updatedAt)
                    const dateA = new Date(a.updatedAt || 0).getTime();
                    const dateB = new Date(b.updatedAt || 0).getTime();
                    comparison = dateA - dateB;
                    break;
                }
            }
            return sortOrder === 'asc' ? comparison : -comparison;
        });
    }, [candidates, searchTerm, selectedSeniority, selectedExperience, sortBy, sortOrder]);

    // Reset Filters
    const resetFilters = () => {
        setSelectedSeniority(null);
        setSelectedExperience(null);
        setSearchTerm('');
    };

    const hasActiveFilters = selectedSeniority !== null || selectedExperience !== null;

    const handleProfileSelect = (profile: DisplayProfile) => {
        if (profile.status === 'analyzing') return;
        setSelectedProfile(profile);
        setIsContactModalOpen(false);
        navigate(`/talents/${profile.id}`, { replace: true });
        // Load full profile (summary_json) for the detail panel
        fetchCandidateDetail(profile.id).then(full => { if (full) setSelectedProfile(full as DisplayProfile); });
    };

    const handleCloseDrawer = () => {
        setSelectedProfile(null);
        setIsContactModalOpen(false);
        navigate('/talents', { replace: true });
    };

    const handleCloseAddModal = () => {
        if (isUploading) return;
        setIsAddModalOpen(false);
        setTimeout(() => setNewCandidateFiles([]), 300);
    };

    const addToast = (message: string, type: 'info' | 'success' | 'error') => {
        const id = Date.now().toString();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 4000);
    };

    // PDF direct export — CV format via react-pdf, with optional anonymization
    const handleDirectPDF = async (isAnonymized: boolean) => {
        if (!selectedProfile) return;
        try {
            setIsExportingPresentation(true);
            const { pdf } = await import('@react-pdf/renderer');
            const { default: CandidateCVPDF } = await import('./CandidateCVPDF');

            const blob = await pdf(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                React.createElement(CandidateCVPDF, {
                    profile: selectedProfile,
                    isAnonymized,
                }) as any
            ).toBlob();

            const nameParts = selectedProfile.name.trim().split(/\s+/);
            const initials = nameParts.map((p: string) => p[0]?.toUpperCase()).slice(0, 2).join('');
            const baseName = isAnonymized ? initials : selectedProfile.name;

            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${baseName}_CV_RevertAI.pdf`;
            a.click();
            URL.revokeObjectURL(url);

            notify('success', isAnonymized ? 'CV anonymisé exporté !' : 'CV exporté avec succès !');
        } catch (error) {
            console.error('PDF export error:', error);
            notify('error', "Erreur lors de l'export PDF");
        } finally {
            setIsExportingPresentation(false);
        }
    };

    // Photo upload handler — stores in Supabase Storage bucket 'candidate-photos'
    const handleUploadPhoto = async (file: File) => {
        if (!selectedProfile) return;
        try {
            const ext = file.name.split('.').pop() ?? 'jpg';
            const path = `${selectedProfile.id}.${ext}`;
            const { error: uploadError } = await supabase.storage
                .from('candidate-photos')
                .upload(path, file, { upsert: true, contentType: file.type });
            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = supabase.storage
                .from('candidate-photos')
                .getPublicUrl(path);

            await updateCandidate(selectedProfile.id, { photo_url: publicUrl });
            await refreshCandidates();
            notify('success', 'Photo mise à jour !');
        } catch (err) {
            console.error(err);
            notify('error', 'Erreur lors de l\'upload de la photo');
        }
    };

    // PDF Export Handler for the Presentation card
    const handleExportPresentation = async () => {
        if (!selectedProfile) return;

        try {
            setIsExportingPresentation(true);
            const { pdf } = await import('@react-pdf/renderer');
            const { default: CandidatePresentationPDF } = await import('./CandidatePresentationPDF');

            const blob = await pdf(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                React.createElement(CandidatePresentationPDF, {
                    profile: selectedProfile,
                    isAnonymized: isPresentationAnonymized,
                }) as any
            ).toBlob();

            const nameParts = selectedProfile.name.trim().split(/\s+/);
            const initials = nameParts.map((p: string) => p[0]?.toUpperCase()).slice(0, 2).join('');
            const baseName = isPresentationAnonymized ? initials : selectedProfile.name;

            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${baseName}_Fiche_RevertAI.pdf`;
            a.click();
            URL.revokeObjectURL(url);

            notify('success', 'Fiche exportée avec succès !');
        } catch (error) {
            console.error('Presentation PDF error:', error);
            notify('error', 'Erreur lors de l\'export PDF');
        } finally {
            setIsExportingPresentation(false);
        }
    };

    // --- GOOGLE DRIVE IMPORT ---
    const handleDriveImport = async () => {
        if (!isDriveConfigured) {
            notify('error', 'Connecteur Google Drive non configuré.', {
                description: 'Définissez VITE_GOOGLE_CLIENT_ID et VITE_GOOGLE_API_KEY.',
            });
            return;
        }
        const driveFiles = await pickDriveFiles();
        if (driveFiles.length === 0) return;

        setNewCandidateFiles((prev) => {
            const seen = new Set(prev.map((f) => `${f.name}::${f.size}`));
            const merged = [...prev];
            for (const f of driveFiles) {
                const key = `${f.name}::${f.size}`;
                if (!seen.has(key)) {
                    seen.add(key);
                    merged.push(f);
                }
            }
            return merged;
        });
        notify('success', `${driveFiles.length} CV importé${driveFiles.length > 1 ? 's' : ''} depuis Drive`, {
            description: "Cliquez sur « Analyser » pour lancer le traitement.",
        });
    };

    // --- ASYNC FIRE & FORGET LOGIC ---
    const handleAnalyzeCandidate = async () => {
        if (newCandidateFiles.length === 0) return;

        // Single file: content-based deduplication
        if (newCandidateFiles.length === 1) {
            const file = newCandidateFiles[0];

            if (fetchedCandidates) {
                const fileHash = await computeFileHash(file);
                // `content_hash` est écrit à l'INSERT, donc AVANT l'analyse : une fiche
                // tombée en erreur en porte un, tout comme une fiche analysée. Sans le
                // filtre sur le statut, redéposer un CV en échec ouvrait sa page vide au
                // lieu de relancer l'analyse (ticket Chloé du 2026-08-20). Seule une fiche
                // réellement analysée dispense de refaire le travail.
                const existing = fetchedCandidates.find(c => c.content_hash === fileHash && c.status === 'completed');

                if (existing) {
                    const updatedAt = existing.updatedAt || new Date().toISOString();
                    const daysDiff = (new Date().getTime() - new Date(updatedAt).getTime()) / (1000 * 3600 * 24);

                    if (daysDiff < 10) {
                        console.log(`Smart Deduplication: Found existing profile for hash ${fileHash.substring(0, 8)} (${Math.round(daysDiff)} days old). Skipping analysis.`);
                        setIsAddModalOpen(false);
                        setNewCandidateFiles([]);
                        notify('info', `Profil existant trouvé (mis à jour il y a ${Math.round(daysDiff)}j)`, {
                            description: "Nous avons affiché la version existante.",
                            action: {
                                label: "Relancer",
                                onClick: async () => {
                                    const result = await uploadCandidate(file);
                                    if (result?.success) notify('success', "Analyse démarrée 🚀");
                                    else notify('error', "Erreur lors de la relance");
                                }
                            },
                            duration: 8000
                        });
                        setSelectedProfile(existing as DisplayProfile);
                        fetchCandidateDetail(existing.id).then(full => { if (full) setSelectedProfile(full as DisplayProfile); });
                        navigate(`/talents/${existing.id}`, { replace: true });
                        return;
                    }
                }
            }

            const result = await uploadCandidate(file);
            if (result?.success) {
                setIsAddModalOpen(false);
                setNewCandidateFiles([]);
                notify('success', "Analyse démarrée 🚀");
            } else {
                notify('error', result?.error || error || "Erreur lors de l'envoi.");
            }
            return;
        }

        // Batch mode: multiple files
        setIsAddModalOpen(false);
        setNewCandidateFiles([]);
        notify('info', `Envoi de ${newCandidateFiles.length} CV en cours…`);

        const results = await uploadCandidates(newCandidateFiles);

        const succeeded = results.filter(r => r.success).length;
        const failed = results.filter(r => !r.success).length;
        const queued = results.filter(r => r.queued).length;

        if (queued > 0) {
            setBatchTick(t => t + 1);
            // Mode lot (Batch API) : traitement asynchrone à coût réduit
            notify(failed === 0 ? 'success' : 'info', `${queued} CV envoyés en traitement par lot`, {
                description: `Les profils apparaîtront automatiquement.${failed > 0 ? ` ${failed} fichier(s) en échec.` : ''}`,
            });
        } else if (failed === 0) {
            notify('success', `${succeeded} CV envoyés 🚀`, { description: "L'analyse est en cours pour chaque profil." });
        } else if (succeeded === 0) {
            notify('error', `Échec de l'envoi des ${failed} CV.`);
        } else {
            notify('info', `${succeeded} CV envoyés, ${failed} en échec.`, {
                description: results.filter(r => !r.success).map(r => r.file).join(', ')
            });
        }
    };

    // Helper to determine skill badge style based on CATEGORY
    const getSkillBadgeStyle = (type: 'technical' | 'methods' | 'soft') => {
        switch (type) {
            case 'technical': return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'methods': return 'bg-green-100 text-green-800 border-green-200';
            case 'soft': return 'bg-orange-100 text-orange-800 border-orange-200';
            default: return 'bg-slate-100 text-slate-700 border-slate-200';
        }
    };

    if (candidatesError) {
        return (
            <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                <AlertTriangle className="text-red-500 mb-4" size={48} />
                <h2 className="text-xl font-bold text-slate-800 mb-2">Erreur de chargement</h2>
                <p className="text-slate-600 mb-4">{candidatesError}</p>
                <button
                    onClick={() => refreshCandidates()}
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                >
                    Réessayer
                </button>
            </div>
        );
    }

    // Vue détail candidat — remplace la liste (plein écran comme une mission)
    if (selectedProfile) {
        return (
            <>
                <CandidateDetailPanel
                    profile={selectedProfile}
                    isContactModalOpen={isContactModalOpen}
                    setIsContactModalOpen={setIsContactModalOpen}
                    isEditingContact={isEditingContact}
                    setIsEditingContact={setIsEditingContact}
                    editedContact={editedContact}
                    setEditedContact={setEditedContact}
                    isSavingContact={isSavingContact}
                    isShareLoading={isShareLoading}
                    onClose={handleCloseDrawer}
                    onExportPDF={handleDirectPDF}
                    isExportingPDF={isExportingPresentation}
                    onOpenPresentation={(isAnonymized) => { setIsPresentationAnonymized(isAnonymized); setIsPresentationOpen(true); }}
                    onShare={handleShare}
                    onGoToMatching={handleGoToMatching}
                    onSaveContact={handleSaveContact}
                    onUploadPhoto={handleUploadPhoto}
                    getSkillBadgeStyle={getSkillBadgeStyle}
                    isEditingAvailability={isEditingAvailability}
                    setIsEditingAvailability={setIsEditingAvailability}
                    editedAvailability={editedAvailability}
                    setEditedAvailability={setEditedAvailability}
                    isSavingAvailability={isSavingAvailability}
                    onSaveAvailability={handleSaveAvailability}
                />
                {isPresentationOpen && (
                    <CandidatePresentation
                        profile={selectedProfile}
                        onClose={() => { setIsPresentationOpen(false); setIsPresentationAnonymized(false); }}
                        onExportPDF={handleExportPresentation}
                        isExporting={isExportingPresentation}
                        isAnonymized={isPresentationAnonymized}
                        onToggleAnonymize={() => setIsPresentationAnonymized(v => !v)}
                    />
                )}
                {isShareModalOpen && shareLink && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity" onClick={() => setIsShareModalOpen(false)}></div>
                        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-6 space-y-6 animate-in zoom-in-95 duration-200">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                                    <Share2 className="text-amber-500" size={20} />
                                    Partager le profil
                                </h3>
                                <button onClick={() => setIsShareModalOpen(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                                    <X size={20} />
                                </button>
                            </div>
                            <div className="space-y-4">
                                <p className="text-sm text-slate-600">
                                    Ce lien permet à des personnes externes de consulter une version simplifiée du profil sans avoir besoin de compte.
                                </p>
                                <div className="flex items-center gap-2 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                                    <input
                                        readOnly
                                        value={shareLink}
                                        className="flex-1 bg-transparent text-sm font-mono text-slate-600 focus:outline-none"
                                        onClick={(e) => e.currentTarget.select()}
                                    />
                                    <button
                                        onClick={() => { navigator.clipboard.writeText(shareLink); }}
                                        className="p-2 hover:bg-slate-200 rounded text-slate-500 hover:text-slate-700 transition-colors"
                                        title="Copier le lien"
                                    >
                                        <Copy size={16} />
                                    </button>
                                </div>
                                <a
                                    href={`mailto:?subject=Candidat intéressant : ${selectedProfile.name}&body=Voici le profil de ${selectedProfile.name} : ${shareLink}`}
                                    className="flex items-center justify-center gap-2 w-full py-2.5 bg-slate-900 text-white rounded-lg font-bold hover:bg-slate-800 transition-colors"
                                >
                                    <Mail size={16} />
                                    Envoyer par email
                                </a>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    }

    return (
        <div className="animate-in fade-in duration-500 relative min-h-full" onClick={() => setActiveDropdown(null)}>

            {/* TOAST CONTAINER */}
            <div className="fixed top-24 right-8 z-[100] flex flex-col gap-3 pointer-events-none">
                {toasts.map(toast => (
                    <div
                        key={toast.id}
                        className={`pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border animate-in slide-in-from-right duration-300 ${toast.type === 'info'
                            ? 'bg-blue-50 border-blue-200 text-blue-800'
                            : toast.type === 'success'
                                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                                : 'bg-red-50 border-red-200 text-red-800'
                            }`}
                    >
                        {toast.type === 'info' ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
                        <span className="font-medium text-sm">{toast.message}</span>
                    </div>
                ))}
            </div>

            {/* Header */}
            <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Talents</h1>
                    <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                        {isCandidatesLoading ? (
                            <div className="flex items-center gap-2">
                                <Loader2 size={14} className="animate-spin" />
                                <span>Chargement des profils...</span>
                            </div>
                        ) : (
                            <span><span className="font-semibold text-slate-900">{candidates.length}</span> Profils Qualifiés</span>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <ScopeToggle scope={scope} onChange={handleScopeChange} orgName={org?.name} entityLabel="Talents" />

                <button
                    onClick={(e) => { e.stopPropagation(); setIsAddModalOpen(true); }}
                    className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2.5 rounded-lg font-medium transition-colors shadow-sm shadow-amber-200"
                >
                    <Plus size={18} />
                    Ajouter Talents
                </button>
                </div>
            </div>

            {/* SEARCH & FILTER BAR */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm mb-6 flex flex-col lg:flex-row gap-4 lg:items-center">
                {/* Search */}
                <div className="relative flex-1">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <Search className="text-amber-500" size={18} />
                    </div>
                    <input
                        type="text"
                        placeholder="Rechercher par nom, rôle ou compétence (ex: React, Manager)..."
                        className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all text-sm text-orange-600 placeholder-slate-400"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {/* Separator */}
                <div className="hidden lg:block w-px h-8 bg-slate-200"></div>

                {/* Filters Group */}
                <div className="flex flex-wrap items-center gap-3">

                    {/* 0. SORT DROPDOWN */}
                    <div className="relative">
                        <button
                            onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === 'sort' ? null : 'sort'); }}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${sortBy !== 'date'
                                ? 'bg-amber-50 text-amber-700 border-amber-300'
                                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                                }`}
                        >
                            <ArrowUpDown size={14} />
                            {sortBy === 'date' ? 'Date' : sortBy === 'name' ? 'Nom' : 'Expérience'}
                            <ChevronDown size={14} className={`transition-transform ${activeDropdown === 'sort' ? 'rotate-180' : ''}`} />
                        </button>

                        {activeDropdown === 'sort' && (
                            <div className="absolute top-full mt-2 w-48 bg-white border border-slate-200 rounded-xl shadow-xl z-50 p-1" onClick={(e) => e.stopPropagation()}>
                                <div className="px-3 py-2 text-xs font-bold text-slate-400 uppercase tracking-wider">Trier par</div>
                                {[
                                    { id: 'date', label: 'Récents' },
                                    { id: 'name', label: 'Nom (A-Z)' },
                                    { id: 'experience', label: 'Expérience' }
                                ].map((opt) => (
                                    <div
                                        key={opt.id}
                                        onClick={() => {
                                            if (sortBy === opt.id) {
                                                // Toggle order if same sort
                                                setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
                                            } else {
                                                setSortBy(opt.id as any);
                                                setSortOrder('asc'); // Default to asc for name/exp, desc for date? Logic handled in sort function
                                                if (opt.id === 'date') setSortOrder('desc');
                                                if (opt.id === 'experience') setSortOrder('desc');
                                            }
                                            setActiveDropdown(null);
                                        }}
                                        className={`flex items-center justify-between p-2 rounded text-sm cursor-pointer hover:bg-slate-50 ${sortBy === opt.id ? 'bg-amber-50 text-amber-700 font-medium' : 'text-slate-600'}`}
                                    >
                                        {opt.label}
                                        {sortBy === opt.id && (
                                            <span className="text-xs bg-white px-1.5 py-0.5 rounded border border-amber-200 text-amber-600">
                                                {sortOrder === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* 1. SENIORITY FILTER (Dynamic) */}
                    <div className="relative">
                        <button
                            onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === 'seniority' ? null : 'seniority'); }}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${selectedSeniority
                                ? 'bg-amber-50 text-amber-700 border-amber-300'
                                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                                }`}
                        >
                            Séniorité
                            {selectedSeniority && <span className="text-xs ml-1 font-bold">({selectedSeniority})</span>}
                            <ChevronDown size={14} className={`transition-transform ${activeDropdown === 'seniority' ? 'rotate-180' : ''}`} />
                        </button>

                        {activeDropdown === 'seniority' && (
                            <div className="absolute top-full mt-2 w-48 bg-white border border-slate-200 rounded-xl shadow-xl z-50 p-1" onClick={(e) => e.stopPropagation()}>
                                <div
                                    onClick={() => setSelectedSeniority(null)}
                                    className={`p-2 rounded text-sm cursor-pointer hover:bg-slate-50 ${!selectedSeniority ? 'bg-slate-50 font-bold text-amber-700' : 'text-slate-600'}`}
                                >
                                    Tous
                                </div>
                                {uniqueSeniority.map(level => (
                                    <div
                                        key={level}
                                        onClick={() => setSelectedSeniority(level)}
                                        className={`flex items-center justify-between p-2 rounded text-sm cursor-pointer hover:bg-slate-50 ${selectedSeniority === level ? 'bg-amber-50 text-amber-700 font-medium' : 'text-slate-600'}`}
                                    >
                                        {level}
                                        {selectedSeniority === level && <Check size={14} />}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* 2. EXPERIENCE FILTER (Range Logic) */}
                    <div className="relative">
                        <button
                            onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === 'experience' ? null : 'experience'); }}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${selectedExperience
                                ? 'bg-amber-50 text-amber-700 border-amber-300'
                                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                                }`}
                        >
                            Expérience
                            {selectedExperience && <span className="text-xs ml-1 font-bold">
                                {selectedExperience === 'junior' ? '(0-3 ans)' : selectedExperience === 'confirmed' ? '(3-7 ans)' : '(+7 ans)'}
                            </span>}
                            <ChevronDown size={14} className={`transition-transform ${activeDropdown === 'experience' ? 'rotate-180' : ''}`} />
                        </button>

                        {activeDropdown === 'experience' && (
                            <div className="absolute top-full mt-2 w-56 bg-white border border-slate-200 rounded-xl shadow-xl z-50 p-1" onClick={(e) => e.stopPropagation()}>
                                <div
                                    onClick={() => setSelectedExperience(null)}
                                    className={`p-2 rounded text-sm cursor-pointer hover:bg-slate-50 ${!selectedExperience ? 'bg-slate-50 font-bold text-amber-700' : 'text-slate-600'}`}
                                >
                                    Tous
                                </div>
                                <div
                                    onClick={() => setSelectedExperience('junior')}
                                    className={`flex justify-between p-2 rounded text-sm cursor-pointer hover:bg-slate-50 ${selectedExperience === 'junior' ? 'bg-amber-50 text-amber-700' : 'text-slate-600'}`}
                                >
                                    Junior (0-3 ans) {selectedExperience === 'junior' && <Check size={14} />}
                                </div>
                                <div
                                    onClick={() => setSelectedExperience('confirmed')}
                                    className={`flex justify-between p-2 rounded text-sm cursor-pointer hover:bg-slate-50 ${selectedExperience === 'confirmed' ? 'bg-amber-50 text-amber-700' : 'text-slate-600'}`}
                                >
                                    Confirmé (3-7 ans) {selectedExperience === 'confirmed' && <Check size={14} />}
                                </div>
                                <div
                                    onClick={() => setSelectedExperience('expert')}
                                    className={`flex justify-between p-2 rounded text-sm cursor-pointer hover:bg-slate-50 ${selectedExperience === 'expert' ? 'bg-amber-50 text-amber-700' : 'text-slate-600'}`}
                                >
                                    Expert (+7 ans) {selectedExperience === 'expert' && <Check size={14} />}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* RESET BUTTON */}
                    {hasActiveFilters && (
                        <button
                            onClick={resetFilters}
                            className="flex items-center gap-1 text-slate-400 hover:text-red-500 text-sm font-medium transition-colors ml-2 animate-in fade-in"
                        >
                            <Trash2 size={14} />
                            Réinitialiser
                        </button>
                    )}
                </div>
            </div>

            {/* Main List */}
            {/* Analysis Progress Widget */}
            <BatchImportWidget
                refreshSignal={batchTick}
                onCandidatesUpdated={refreshCandidates}
                sendingCount={isUploading && batchProgress && batchProgress.total >= BATCH_MODE_THRESHOLD ? batchProgress.total : 0}
            />
            <AnalysisProgressWidget />

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[500px]">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            <th className="px-4 py-4 w-[18%]">Talent</th>
                            <th className="px-4 py-4 w-[15%]">Rôle</th>
                            <th className="px-4 py-4 w-[37%] hidden md:table-cell">Top Compétences</th>
                            <th className="px-4 py-4 w-[10%] text-center hidden sm:table-cell">Expérience</th>
                            <th className="px-4 py-4 w-[10%]">Analyse</th>
                            <th className="px-4 py-4 w-[10%]">Assigné à</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {isCandidatesLoading ? (
                            // Loading Skeletons
                            Array.from({ length: 5 }).map((_, idx) => (
                                <tr key={idx} className="animate-pulse border-b border-slate-50 last:border-0">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-slate-200"></div>
                                            <div className="h-4 w-32 bg-slate-200 rounded"></div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4"><div className="h-4 w-24 bg-slate-200 rounded"></div></td>
                                    <td className="px-6 py-4 hidden md:table-cell"><div className="h-4 w-48 bg-slate-200 rounded"></div></td>
                                    <td className="px-6 py-4 hidden sm:table-cell"><div className="h-4 w-12 bg-slate-200 rounded mx-auto"></div></td>
                                    <td className="px-6 py-4"><div className="h-4 w-16 bg-slate-200 rounded"></div></td>
                                </tr>
                            ))
                        ) : filteredCandidates.length > 0 ? (
                            filteredCandidates.map((profile) => {
                                // Calculate global match score specific to this view context (generic match)
                                // For now, we use the profile.score if available, or calculate average
                                const score = calculateScore(profile);

                                // 1. Merge all skills across categories, sorted by score, top 5
                                let topSkills: { name: string; level: number; type: 'technical' | 'methods' | 'soft' }[] = [];

                                // 1. Merge all skills across categories
                                const allSkills = [
                                    ...profile.skills.technical.map(s => ({ ...s, type: 'technical' as const })),
                                    ...profile.skills.methods.map(s => ({ ...s, type: 'methods' as const })),
                                    ...profile.skills.soft.map(s => ({ ...s, type: 'soft' as const })),
                                ];

                                // Deduplicate by name to prevent key conflicts
                                const uniqueSkills = Array.from(new Map(allSkills.map(s => [s.name, s])).values());

                                topSkills = uniqueSkills
                                    .sort((a, b) => b.level - a.level)
                                    .slice(0, 5)
                                    .sort((a, b) => {
                                        const typeOrder = { technical: 0, methods: 1, soft: 2 };
                                        return typeOrder[a.type] - typeOrder[b.type];
                                    });

                                const isAnalyzing = false; // We use the widget for analyzing status now

                                return (
                                    <tr
                                        key={profile.id}
                                        className={`transition-colors border-b border-slate-50 last:border-0 ${isAnalyzing
                                            ? 'bg-slate-50/50 cursor-wait'
                                            : `hover:bg-amber-50/30 cursor-pointer group ${selectedProfile?.id === profile.id ? 'bg-amber-50' : ''}`
                                            }`}
                                        onClick={() => handleProfileSelect(profile)}
                                    >
                                        <td className="px-4 py-4 truncate">
                                            <div className="flex items-center gap-3">
                                                {isAnalyzing ? (
                                                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center border border-slate-200 shrink-0">
                                                        <Loader2 size={18} className="animate-spin text-slate-400" />
                                                    </div>
                                                ) : (
                                                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-bold border border-slate-200 group-hover:border-amber-300 group-hover:text-amber-700 transition-colors shrink-0">
                                                        {profile.name.substring(0, 2).toUpperCase()}
                                                    </div>
                                                )}

                                                <div className="flex flex-col truncate">
                                                    <span className={`font-bold text-xs truncate ${isAnalyzing ? 'text-slate-500 italic' : 'text-slate-900'}`}>
                                                        {profile.name}
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            {isAnalyzing ? (
                                                <div className="h-4 w-24 bg-slate-200 rounded animate-pulse"></div>
                                            ) : (
                                                <span className="text-slate-900 font-bold text-xs truncate">{profile.role}</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-4 hidden md:table-cell">
                                            {isAnalyzing ? (
                                                <span className="text-slate-300 font-mono">---</span>
                                            ) : (
                                                <div className="flex flex-wrap items-center gap-1.5 w-full max-h-[52px] overflow-hidden">
                                                    {topSkills.map(s => (
                                                        <span
                                                            key={s.name}
                                                            className={`flex-shrink-0 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide border rounded-full whitespace-nowrap ${getSkillBadgeStyle(s.type)}`}
                                                        >
                                                            {s.name}
                                                        </span>
                                                    ))}
                                                    {topSkills.length === 0 && (
                                                        <span className="text-slate-400 text-xs italic">—</span>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-4 hidden sm:table-cell">
                                            {isAnalyzing ? (
                                                <div className="flex flex-col items-center gap-1 opacity-50">
                                                    <div className="h-3 w-8 bg-slate-200 rounded"></div>
                                                </div>
                                            ) : (
                                                <div className="flex flex-col items-center justify-center gap-1">
                                                    <span className="text-slate-700 text-xs font-bold">{profile.experience}</span>
                                                    {profile.seniority_level && (
                                                        <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px] font-semibold capitalize border border-slate-200 whitespace-nowrap">
                                                            {profile.seniority_level}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-4">
                                            <button
                                                disabled={isAnalyzing}
                                                className={`font-medium text-xs flex items-center gap-1 transition-colors ${isAnalyzing
                                                    ? 'text-slate-300 cursor-not-allowed'
                                                    : 'text-orange-600 hover:underline'
                                                    }`}
                                            >
                                                Voir Analyse <ChevronRight size={16} />
                                            </button>
                                        </td>
                                        <td className="px-4 py-4">
                                            {!isAnalyzing && (
                                                <AssignDropdown
                                                    table="candidates"
                                                    rowId={profile.id}
                                                    assignedTo={profile.assigned_to ?? null}
                                                    members={orgMembers}
                                                    onAssigned={() => refreshCandidates()}
                                                    align="right"
                                                />
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={6} className="py-12 text-center text-slate-500 bg-slate-50/50">
                                    <div className="flex flex-col items-center justify-center">
                                        <div className="w-12 h-12 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mb-3">
                                            <div className="opacity-50">
                                                <Search size={24} />
                                            </div>
                                        </div>
                                        <p className="text-sm font-medium">Aucun talent ne correspond à ces critères.</p>
                                        <button onClick={resetFilters} className="mt-2 text-xs text-amber-600 font-semibold hover:underline">Réinitialiser les filtres</button>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* 
        ========================================
        ADD CANDIDATE MODAL
        ========================================
      */}
            {isAddModalOpen && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={(e) => e.stopPropagation()}>
                    {/* Backdrop */}
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={handleCloseAddModal}></div>

                    {/* Modal */}
                    <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col z-10">
                        {/* Header */}
                        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                            <h3 className="font-bold text-lg text-slate-900">Ajouter un nouveau talent</h3>
                            <button onClick={handleCloseAddModal} className="p-1 rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-600 transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        {/* Body - Dropzone */}
                        <div className="p-6">
                            <FileDropZone
                                label=""
                                subLabel="Formats supportés : PDF, DOCX — glissez plusieurs CV ou un dossier entier"
                                accept=".pdf,.doc,.docx"
                                multiple={true}
                                allowDirectory={true}
                                files={newCandidateFiles}
                                onFilesChange={setNewCandidateFiles}
                                icon={<UploadCloud size={32} />}
                            />

                            {/* Au-delà du seuil, l'envoi change de nature : un seul job part
                                chez Google au lieu de N analyses concurrentes. Le dire avant
                                de cliquer, pas après. */}
                            {newCandidateFiles.length >= BATCH_MODE_THRESHOLD && (
                                <div className="mt-4 flex items-center gap-3 rounded-lg border border-amber-200 border-l-4 border-l-amber-500 bg-amber-50 px-4 py-3">
                                    <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
                                        <Info size={16} className="text-amber-600" />
                                    </span>
                                    <p className="text-sm leading-relaxed text-slate-700">
                                        <span className="font-semibold text-amber-800">Traitement par lot</span> : les profils s'afficheront au fur et à mesure.
                                    </p>
                                </div>
                            )}

                            {/* Cloud connectors */}
                            <div className="mt-4 flex items-center gap-3">
                                <div className="flex-1 h-px bg-slate-100" />
                                <span className="text-xs text-slate-400">ou importer depuis</span>
                                <div className="flex-1 h-px bg-slate-100" />
                            </div>
                            <button
                                type="button"
                                onClick={handleDriveImport}
                                disabled={isDrivePicking || isUploading || !isDriveConfigured}
                                title={isDriveConfigured ? 'Importer des CV depuis Google Drive' : 'Connecteur Google Drive non configuré'}
                                className={`mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border font-medium transition-colors ${
                                    isDrivePicking || isUploading || !isDriveConfigured
                                        ? 'border-slate-200 text-slate-400 bg-slate-50 cursor-not-allowed'
                                        : 'border-slate-300 text-slate-700 bg-white hover:border-amber-400 hover:text-amber-600'
                                }`}
                            >
                                {isDrivePicking ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        {driveProgress
                                            ? `Téléchargement ${driveProgress.current}/${driveProgress.total}…`
                                            : 'Connexion à Google Drive…'}
                                    </>
                                ) : (
                                    <>
                                        <UploadCloud size={16} />
                                        Google Drive
                                    </>
                                )}
                            </button>
                            {!isDriveConfigured ? (
                                <p className="mt-2 text-xs text-slate-400 text-center">
                                    Connecteur Drive non configuré sur cet environnement.
                                </p>
                            ) : (
                                <div className="mt-2 flex items-start justify-center gap-1.5 text-xs text-amber-600">
                                    <Info size={13} className="shrink-0 mt-px" />
                                    <span className="text-left leading-snug">
                                        Connexion Google Drive en accès anticipé
                                        <br />
                                        Réservée aux comptes autorisés
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-3">
                            <button
                                onClick={handleCloseAddModal}
                                disabled={isUploading}
                                className="px-4 py-2 text-slate-600 font-medium hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleAnalyzeCandidate}
                                disabled={newCandidateFiles.length === 0 || isUploading}
                                className={`px-4 py-2 rounded-lg font-bold text-white transition-all shadow-sm flex items-center gap-2 ${(newCandidateFiles.length > 0 && !isUploading)
                                    ? 'bg-amber-600 hover:bg-amber-700 hover:shadow-md'
                                    : 'bg-slate-300 cursor-not-allowed'
                                    }`}
                            >
                                {isUploading ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        {batchProgress
                                            ? `Envoi ${batchProgress.current}/${batchProgress.total}…`
                                            : 'Envoi…'
                                        }
                                    </>
                                ) : (
                                    <>
                                        <Sparkles size={16} />
                                        {newCandidateFiles.length > 1
                                            ? `Analyser ${newCandidateFiles.length} CV`
                                            : 'Analyser le CV'
                                        }
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* File Drop Zone */}
            <div className={`mb-8 transition-all duration-300 ease-in-out overflow-hidden ${isAddModalOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="bg-white p-6 rounded-xl border border-dashed border-indigo-200 shadow-sm">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                            <UploadCloud className="text-indigo-600" />
                            Ajouter un nouveau talent
                        </h2>
                        <button onClick={() => setIsAddModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                            <X size={20} />
                        </button>
                    </div>
                    <FileDropZone
                        label="CV du candidat"
                        subLabel="PDF (Max 10MB)"
                        accept=".pdf"
                        files={newCandidateFiles}
                        onFilesChange={(files) => {
                            setNewCandidateFiles(files);
                            // Auto-analyze if a file is added
                            if (files.length > 0) {
                                // We need to pass the file to uploadCandidate, but handleAnalyzeCandidate uses newCandidateFiles state
                                // which might not be updated yet in this closure. 
                                // Better to just set files here, and have a "Analyze" button, OR
                                // adapt handleAnalyzeCandidate to accept a file argument.
                                // For now, let's keep it simple: Select file -> trigger effect or user clicks something?
                                // The user requests "non-blocking", usually implied drag & drop -> start.

                                // Actually, handleAnalyzeCandidate reads from state `newCandidateFiles`.
                                // State updates are async.
                                // Let's try to call upload directly with the new file or wait for the user to confirm?
                                // User flow: "Ajouter Talents" -> Modal -> Drop -> ?
                                // The layout shows a Modal. 

                                // Let's check handleAnalyzeCandidate again.
                                // It takes file from state.

                                // To fix the crash immediately: pass `files={newCandidateFiles}`.
                                // To restore functionality: ensure upload triggers.
                            }
                        }}
                    />
                    {/* Action Buttons for the Modal */}
                    <div className="flex justify-end gap-3 mt-4">
                        <button
                            onClick={() => {
                                setNewCandidateFiles([]);
                                setIsAddModalOpen(false);
                            }}
                            className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                            Annuler
                        </button>
                        <button
                            onClick={handleAnalyzeCandidate}
                            disabled={newCandidateFiles.length === 0 || isUploading}
                            className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${newCandidateFiles.length === 0 || isUploading
                                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                : 'bg-indigo-600 text-white hover:bg-indigo-700'
                                }`}
                        >
                            {isUploading ? <Loader2 size={18} className="animate-spin" /> : <Rocket size={18} />}
                            Lancer l'analyse
                        </button>
                    </div>
                    {error && (
                        <div className="mt-3 p-3 bg-red-50 text-red-600 text-sm rounded-lg flex items-center gap-2">
                            <AlertTriangle size={16} />
                            {error}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TalentPoolView;
